import { z } from "zod";
import { bookGenerationSourceBindingV1Schema } from "./book-generation.js";
import { bookProductionProductSchema, bookProductionVersionBindingV1Schema, } from "./book-production-evidence.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const opaqueIdSchema = z.string().trim().min(1).max(240);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
const semanticFactCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Z][A-Z0-9_.]*$/u);
function unique(values, context, path, label) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: `${label} must be unique.` });
}
const uniqueIds = (minimum = 1, maximum = 40) => z
    .array(opaqueIdSchema)
    .min(minimum)
    .max(maximum)
    .superRefine((values, context) => unique(values, context, [], "Signal IDs"));
export const bookVisualIntentContractSchema = z.literal("BOOK_VISUAL_INTENT_V1");
export const bookVisualImplementationPlanContractSchema = z.literal("BOOK_VISUAL_IMPLEMENTATION_PLAN_V1");
export const bookVisualImplementationTraceContractSchema = z.literal("BOOK_VISUAL_IMPLEMENTATION_TRACE_V1");
export const bookVisualImplementationRequestContractSchema = z.literal("BOOK_VISUAL_IMPLEMENTATION_REQUEST_V1");
export const bookVisualIntentAxisSchema = z.enum([
    "TYPOGRAPHY",
    "GRID",
    "INFORMATION_ARCHITECTURE",
    "COVER_GRAMMAR",
    "PAGE_FAMILY_GRAMMAR",
    "EVIDENCE_MEDIA_TREATMENT",
    "NAVIGATION",
    "DENSITY",
    "WHITESPACE_CADENCE",
    "COLOR",
    "ILLUSTRATION_LANGUAGE",
    "PACING",
    "ACCESSIBILITY",
    "OUTPUT_FORMAT",
]);
export const bookVisualIntentSignalOriginSchema = z.enum([
    "CUSTOMER_AUTHORED",
    "CUSTOMER_SELECTED",
    "CUSTOMER_APPROVED",
    "SOURCE_DERIVED",
    "PRODUCT_POLICY",
]);
export const bookVisualIntentSignalUseSchema = z.enum([
    "DIRECTION_SELECTION",
    "IMPLEMENTATION",
    "CONSTRAINT",
    "HUMAN_REVIEW",
]);
export const bookVisualIntentSignalV1Schema = z
    .object({
    signalId: opaqueIdSchema,
    role: safeCodeSchema,
    origin: bookVisualIntentSignalOriginSchema,
    valueDigestSha256: sha256Schema,
    influences: z.array(bookVisualIntentAxisSchema).min(1).max(14),
    requiredUses: z.array(bookVisualIntentSignalUseSchema).min(1).max(4),
    privacy: z.enum(["PUBLIC_SAFE", "PRIVATE_RUNTIME_ONLY"]),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.influences, context, ["influences"], "Intent influences");
    unique(value.requiredUses, context, ["requiredUses"], "Intent signal uses");
});
const bedtimeVisualIntentSchema = z
    .object({
    product: z.literal("BEDTIME"),
    developmentAndAgeSignalIds: uniqueIds(),
    emotionalArcAndLandingSignalIds: uniqueIds(),
    worldCharacterAndLikenessSignalIds: uniqueIds(),
    illustrationLanguageSignalIds: uniqueIds(),
    pageTurnAndPacingSignalIds: uniqueIds(),
    childGuardianConstraintSignalIds: uniqueIds(),
})
    .strict();
const comicVisualIntentSchema = z
    .object({
    product: z.literal("COMIC"),
    genreAndEraSignalIds: uniqueIds(),
    markMakingAndColorSignalIds: uniqueIds(),
    panelRhythmAndCameraSignalIds: uniqueIds(),
    letteringLanguageSignalIds: uniqueIds(),
    continuityConstraintSignalIds: uniqueIds(),
})
    .strict();
const recipeVisualIntentSchema = z
    .object({
    product: z.literal("RECIPE"),
    cuisineAndEraSignalIds: uniqueIds(),
    familyArchiveSignalIds: uniqueIds(),
    mediaAvailabilitySignalIds: uniqueIds(),
    cookingReadabilitySignalIds: uniqueIds(),
    safetyHierarchySignalIds: uniqueIds(),
    noPhotoTreatmentSignalIds: uniqueIds(),
})
    .strict();
const myStoryVisualIntentSchema = z
    .object({
    product: z.literal("MY_STORY"),
    voiceAndReadingSignalIds: uniqueIds(),
    chronologyAndCorrectionSignalIds: uniqueIds(),
    evidenceTreatmentSignalIds: uniqueIds(),
    documentaryMediaSignalIds: uniqueIds(),
    privacyConstraintSignalIds: uniqueIds(),
    mediaCadenceSignalIds: uniqueIds(),
})
    .strict();
const ourStoryVisualIntentSchema = z
    .object({
    product: z.literal("OUR_STORY"),
    twoPrincipalIdentitySignalIds: uniqueIds(),
    voiceBalanceSignalIds: uniqueIds(),
    mutualMediaApprovalSignalIds: uniqueIds(),
    differingMemorySignalIds: uniqueIds(),
    pairedCompositionSignalIds: uniqueIds(),
    formatEligibilitySignalIds: uniqueIds(),
})
    .strict();
export const bookVisualProductIntentV1Schema = z.discriminatedUnion("product", [
    bedtimeVisualIntentSchema,
    comicVisualIntentSchema,
    recipeVisualIntentSchema,
    myStoryVisualIntentSchema,
    ourStoryVisualIntentSchema,
]);
export const bookVisualDirectionSelectionV1Schema = z
    .object({
    directionId: opaqueIdSchema,
    mode: z.enum(["CUSTOMER_SELECTED", "CUSTOMER_APPROVED_RECOMMENDATION", "DOCUMENTED_FALLBACK"]),
    definitionBinding: bookProductionVersionBindingV1Schema,
    selectionPolicyBinding: bookProductionVersionBindingV1Schema,
    selectionSignalIds: z.array(opaqueIdSchema).max(100),
    fallbackReasonCode: safeCodeSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.selectionSignalIds, context, ["selectionSignalIds"], "Selection signal IDs");
    if (value.mode === "DOCUMENTED_FALLBACK" && !value.fallbackReasonCode)
        context.addIssue({
            code: "custom",
            path: ["fallbackReasonCode"],
            message: "A documented fallback requires an explicit reason code.",
        });
    if (value.mode !== "DOCUMENTED_FALLBACK" && value.fallbackReasonCode)
        context.addIssue({
            code: "custom",
            path: ["fallbackReasonCode"],
            message: "Only a documented fallback may carry a fallback reason.",
        });
    if (value.mode !== "DOCUMENTED_FALLBACK" && value.selectionSignalIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["selectionSignalIds"],
            message: "A customer-selected direction requires exact selection signals.",
        });
});
function productSignalIds(productIntent) {
    return Object.entries(productIntent)
        .filter(([key]) => key !== "product")
        .flatMap(([, value]) => value);
}
const bookVisualIntentBodyV1Schema = z
    .object({
    contract: bookVisualIntentContractSchema,
    intentId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    compilerBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    signals: z.array(bookVisualIntentSignalV1Schema).min(1).max(300),
    productIntent: bookVisualProductIntentV1Schema,
    directionSelection: bookVisualDirectionSelectionV1Schema,
    truthBoundary: z.literal("SUPPLIED_CONTEXT_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.signals.map(({ signalId }) => signalId), context, ["signals"], "Intent signal IDs");
    if (value.productIntent.product !== value.product)
        context.addIssue({
            code: "custom",
            path: ["productIntent", "product"],
            message: "Product intent must use the same product discriminator as the intent record.",
        });
    const knownSignals = new Map(value.signals.map((signal) => [signal.signalId, signal]));
    const categorized = productSignalIds(value.productIntent);
    unique(categorized, context, ["productIntent"], "Signals classified across product-owned intent categories");
    for (const signalId of categorized)
        if (!knownSignals.has(signalId))
            context.addIssue({
                code: "custom",
                path: ["productIntent"],
                message: `Product intent references unknown signal ${signalId}.`,
            });
    for (const signal of value.signals)
        if (!categorized.includes(signal.signalId))
            context.addIssue({
                code: "custom",
                path: ["signals"],
                message: `Signal ${signal.signalId} is not classified by the product-owned intent payload.`,
            });
    for (const signalId of value.directionSelection.selectionSignalIds)
        if (!knownSignals.has(signalId))
            context.addIssue({
                code: "custom",
                path: ["directionSelection", "selectionSignalIds"],
                message: `Direction selection references unknown signal ${signalId}.`,
            });
    if (value.directionSelection.mode !== "DOCUMENTED_FALLBACK") {
        const hasCustomerSignal = value.directionSelection.selectionSignalIds.some((signalId) => {
            const signal = knownSignals.get(signalId);
            return (signal?.origin.startsWith("CUSTOMER_") === true &&
                signal.requiredUses.includes("DIRECTION_SELECTION"));
        });
        if (!hasCustomerSignal)
            context.addIssue({
                code: "custom",
                path: ["directionSelection", "selectionSignalIds"],
                message: "A non-fallback direction must bind at least one customer-origin signal explicitly required for direction selection.",
            });
    }
});
export const bookVisualIntentV1Schema = bookVisualIntentBodyV1Schema.safeExtend({
    intentDigestSha256: sha256Schema,
});
export const bookVisualTokenClassSchema = z.enum([
    "COLOR",
    "TYPOGRAPHY",
    "SPACING",
    "GEOMETRY",
    "RULE",
    "MEDIA_TREATMENT",
    "NAVIGATION",
]);
export const bookVisualFieldSourceClassSchema = z.enum([
    "CUSTOMER_AUTHORED",
    "CUSTOMER_APPROVED_LABEL",
    "EDITORIAL_PUBLIC_COPY",
    "INTERNAL_IDENTIFIER",
    "PROVENANCE_SUMMARY",
    "TEMPORAL_MARKER",
    "AUDIO_TIMECODE",
    "SAFETY_NOTICE",
    "ATTRIBUTION",
]);
export const bookVisualFieldPresentationSchema = z.enum([
    "VERBATIM_APPROVED",
    "CUSTOMER_DISPLAY_LABEL",
    "HUMAN_READABLE_PROJECTION",
    "OMITTED",
]);
export const bookVisualCapacityActionSchema = z.enum([
    "ALLOW_AUTHORED_WHITESPACE",
    "SELECT_ALTERNATE_GRAMMAR",
    "ADD_PAGE_WITH_STABLE_CONTINUATION",
    "BLOCK_RENDER",
]);
const purposesSchema = z
    .array(z.enum([
    "READING",
    "NAVIGATION",
    "ATTRIBUTION",
    "CHRONOLOGY",
    "EVIDENCE",
    "SAFETY",
    "PACING",
    "VOICE",
    "ACCESSIBILITY",
    "PRODUCTION",
]))
    .min(1)
    .max(10);
export const bookVisualImplementationTokenV1Schema = z
    .object({
    tokenId: opaqueIdSchema,
    tokenClass: bookVisualTokenClassSchema,
    valueDigestSha256: sha256Schema,
    intentSignalIds: z.array(opaqueIdSchema).max(100),
})
    .strict()
    .superRefine((value, context) => unique(value.intentSignalIds, context, ["intentSignalIds"], "Token intent signal IDs"));
export const bookVisualVisibleFieldPolicyV1Schema = z
    .object({
    fieldKind: safeCodeSchema,
    sourceClass: bookVisualFieldSourceClassSchema,
    allowedPresentations: z.array(bookVisualFieldPresentationSchema).min(1).max(4),
    requiredSemanticFact: semanticFactCodeSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.allowedPresentations, context, ["allowedPresentations"], "Allowed field presentations");
    if (value.sourceClass === "INTERNAL_IDENTIFIER" &&
        value.allowedPresentations.includes("VERBATIM_APPROVED"))
        context.addIssue({
            code: "custom",
            path: ["allowedPresentations"],
            message: "Internal identifiers may not be rendered verbatim to a customer.",
        });
    if (value.sourceClass === "AUDIO_TIMECODE" && !value.requiredSemanticFact)
        context.addIssue({
            code: "custom",
            path: ["requiredSemanticFact"],
            message: "A timecode-like field requires an exact semantic fact binding.",
        });
});
export const bookVisualImplementationComponentV1Schema = z
    .object({
    componentId: opaqueIdSchema,
    implementationBinding: bookProductionVersionBindingV1Schema,
    classification: z.enum(["STRUCTURAL", "FUNCTIONAL", "DECORATIVE"]),
    purposes: purposesSchema,
    allowedProductPageRoles: z.array(safeCodeSchema).min(1).max(100),
    requiredSemanticFacts: z.array(semanticFactCodeSchema).max(40),
    visibleFieldPolicies: z.array(bookVisualVisibleFieldPolicyV1Schema).max(40),
    intentSignalIds: z.array(opaqueIdSchema).max(100),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.purposes, context, ["purposes"], "Component purposes");
    unique(value.allowedProductPageRoles, context, ["allowedProductPageRoles"], "Allowed product page roles");
    unique(value.requiredSemanticFacts, context, ["requiredSemanticFacts"], "Required semantic facts");
    unique(value.visibleFieldPolicies.map(({ fieldKind }) => fieldKind), context, ["visibleFieldPolicies"], "Visible field policy kinds");
    unique(value.intentSignalIds, context, ["intentSignalIds"], "Component intent signal IDs");
});
const metricRangeSchema = z
    .object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() })
    .strict()
    .refine(({ min, max }) => min <= max, "A capacity range must be ordered minimum to maximum.");
export const bookVisualGrammarCapacityV1Schema = z
    .object({
    textCharacters: metricRangeSchema,
    itemCount: metricRangeSchema,
    mediaCount: metricRangeSchema,
    underfillAction: bookVisualCapacityActionSchema,
    overflowAction: bookVisualCapacityActionSchema,
})
    .strict();
export const bookVisualImplementationGrammarV1Schema = z
    .object({
    grammarId: opaqueIdSchema,
    productPageRole: safeCodeSchema,
    purposes: purposesSchema,
    componentIds: z.array(opaqueIdSchema).min(1).max(100),
    tokenIds: z.array(opaqueIdSchema).min(1).max(100),
    requiredSemanticFacts: z.array(semanticFactCodeSchema).max(40),
    intentSignalIds: z.array(opaqueIdSchema).max(100),
    capacity: bookVisualGrammarCapacityV1Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.purposes, context, ["purposes"], "Grammar purposes");
    unique(value.componentIds, context, ["componentIds"], "Grammar component IDs");
    unique(value.tokenIds, context, ["tokenIds"], "Grammar token IDs");
    unique(value.requiredSemanticFacts, context, ["requiredSemanticFacts"], "Grammar semantic facts");
    unique(value.intentSignalIds, context, ["intentSignalIds"], "Grammar intent signal IDs");
});
export const bookVisualIntentConstraintV1Schema = z
    .object({
    constraintId: opaqueIdSchema,
    intentSignalIds: z.array(opaqueIdSchema).min(1).max(100),
    enforcement: z.enum(["SCHEMA", "RENDER_BLOCK", "QA_BLOCK", "HUMAN_REVIEW"]),
    policyBinding: bookProductionVersionBindingV1Schema,
})
    .strict()
    .superRefine((value, context) => unique(value.intentSignalIds, context, ["intentSignalIds"], "Constraint intent signal IDs"));
const bookVisualImplementationPlanBodyV1Schema = z
    .object({
    contract: bookVisualImplementationPlanContractSchema,
    planId: opaqueIdSchema,
    intentId: opaqueIdSchema,
    intentDigestSha256: sha256Schema,
    directionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    rendererBinding: bookProductionVersionBindingV1Schema,
    configurationBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    fingerprintDigestSha256: sha256Schema,
    requiredDirectionFlags: z
        .array(z
        .object({
        path: z.string().trim().min(1).max(300),
        expectedValueDigestSha256: sha256Schema,
        consumers: z
            .array(z
            .object({
            productPageRole: safeCodeSchema,
            grammarId: opaqueIdSchema,
            componentId: opaqueIdSchema,
        })
            .strict())
            .min(1)
            .max(300),
    })
        .strict()
        .superRefine((flag, context) => {
        unique(flag.consumers.map(({ productPageRole, grammarId, componentId }) => `${productPageRole}:${grammarId}:${componentId}`), context, ["consumers"], "Direction flag consumers");
    }))
        .min(1)
        .max(100),
    requiredProductPageRoles: z.array(safeCodeSchema).min(1).max(100),
    tokens: z.array(bookVisualImplementationTokenV1Schema).min(1).max(300),
    components: z.array(bookVisualImplementationComponentV1Schema).min(1).max(300),
    grammars: z.array(bookVisualImplementationGrammarV1Schema).min(1).max(300),
    intentConstraints: z.array(bookVisualIntentConstraintV1Schema).max(300),
    unmappedRoleAction: z.literal("BLOCK_RENDER"),
    truthBoundary: z.literal("SUPPLIED_BINDINGS_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.requiredDirectionFlags.map(({ path }) => path), context, ["requiredDirectionFlags"], "Required direction flag paths");
    unique(value.requiredProductPageRoles, context, ["requiredProductPageRoles"], "Required product page roles");
    unique(value.tokens.map(({ tokenId }) => tokenId), context, ["tokens"], "Implementation token IDs");
    unique(value.components.map(({ componentId }) => componentId), context, ["components"], "Implementation component IDs");
    unique(value.grammars.map(({ grammarId }) => grammarId), context, ["grammars"], "Implementation grammar IDs");
    unique(value.intentConstraints.map(({ constraintId }) => constraintId), context, ["intentConstraints"], "Intent constraint IDs");
    const requiredRoles = new Set(value.requiredProductPageRoles);
    const tokenIds = new Set(value.tokens.map(({ tokenId }) => tokenId));
    const componentById = new Map(value.components.map((component) => [component.componentId, component]));
    const grammarById = new Map(value.grammars.map((grammar) => [grammar.grammarId, grammar]));
    for (const [flagIndex, flag] of value.requiredDirectionFlags.entries())
        for (const [consumerIndex, consumer] of flag.consumers.entries()) {
            const issuePath = ["requiredDirectionFlags", flagIndex, "consumers", consumerIndex];
            if (!requiredRoles.has(consumer.productPageRole))
                context.addIssue({
                    code: "custom",
                    path: [...issuePath, "productPageRole"],
                    message: `Direction flag ${flag.path} references undeclared product page role ${consumer.productPageRole}.`,
                });
            const grammar = grammarById.get(consumer.grammarId);
            if (!grammar)
                context.addIssue({
                    code: "custom",
                    path: [...issuePath, "grammarId"],
                    message: `Direction flag ${flag.path} references unknown grammar ${consumer.grammarId}.`,
                });
            else {
                if (grammar.productPageRole !== consumer.productPageRole)
                    context.addIssue({
                        code: "custom",
                        path: [...issuePath, "productPageRole"],
                        message: `Direction flag ${flag.path} consumer role does not match grammar ${consumer.grammarId}.`,
                    });
                if (!grammar.componentIds.includes(consumer.componentId))
                    context.addIssue({
                        code: "custom",
                        path: [...issuePath, "componentId"],
                        message: `Direction flag ${flag.path} consumer component ${consumer.componentId} is not used by grammar ${consumer.grammarId}.`,
                    });
            }
            if (!componentById.has(consumer.componentId))
                context.addIssue({
                    code: "custom",
                    path: [...issuePath, "componentId"],
                    message: `Direction flag ${flag.path} references unknown component ${consumer.componentId}.`,
                });
        }
    for (const [index, grammar] of value.grammars.entries()) {
        if (!requiredRoles.has(grammar.productPageRole))
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "productPageRole"],
                message: "Every grammar role must be declared in requiredProductPageRoles.",
            });
        for (const componentId of grammar.componentIds) {
            const component = componentById.get(componentId);
            if (!component)
                context.addIssue({
                    code: "custom",
                    path: ["grammars", index, "componentIds"],
                    message: `Grammar references unknown component ${componentId}.`,
                });
            else if (!component.allowedProductPageRoles.includes(grammar.productPageRole))
                context.addIssue({
                    code: "custom",
                    path: ["grammars", index, "componentIds"],
                    message: `${componentId} is not allowed on ${grammar.productPageRole}.`,
                });
        }
        for (const tokenId of grammar.tokenIds)
            if (!tokenIds.has(tokenId))
                context.addIssue({
                    code: "custom",
                    path: ["grammars", index, "tokenIds"],
                    message: `Grammar references unknown token ${tokenId}.`,
                });
    }
    for (const role of requiredRoles)
        if (!value.grammars.some(({ productPageRole }) => productPageRole === role))
            context.addIssue({
                code: "custom",
                path: ["grammars"],
                message: `Required product page role ${role} has no visual grammar.`,
            });
});
export const bookVisualImplementationPlanV1Schema = bookVisualImplementationPlanBodyV1Schema.safeExtend({ planDigestSha256: sha256Schema });
export const bookVisualImplementationMetricsV1Schema = z
    .object({
    textCharacters: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
    mediaCount: z.number().int().nonnegative(),
})
    .strict();
export const bookVisualImplementationRequestV1Schema = z
    .object({
    contract: bookVisualImplementationRequestContractSchema,
    requestId: opaqueIdSchema,
    product: bookProductionProductSchema,
    intentId: opaqueIdSchema,
    intentDigestSha256: sha256Schema,
    directionId: opaqueIdSchema,
    planDigestSha256: sha256Schema,
    semanticPages: z
        .array(z
        .object({
        semanticPageId: opaqueIdSchema,
        productPageRole: safeCodeSchema,
        metrics: bookVisualImplementationMetricsV1Schema,
        semanticFacts: z.array(semanticFactCodeSchema).max(100),
    })
        .strict())
        .min(1)
        .max(2_000),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.semanticPages.map(({ semanticPageId }) => semanticPageId), context, ["semanticPages"], "Semantic page IDs");
    value.semanticPages.forEach((page, index) => unique(page.semanticFacts, context, ["semanticPages", index, "semanticFacts"], "Semantic facts"));
});
export const bookVisualVisibleFieldUseV1Schema = z
    .object({
    componentId: opaqueIdSchema,
    fieldKind: safeCodeSchema,
    sourceClass: bookVisualFieldSourceClassSchema,
    presentation: bookVisualFieldPresentationSchema,
})
    .strict();
const bookVisualImplementationTraceBodyV1Schema = z
    .object({
    contract: bookVisualImplementationTraceContractSchema,
    traceId: opaqueIdSchema,
    planId: opaqueIdSchema,
    planDigestSha256: sha256Schema,
    fingerprintDigestSha256: sha256Schema,
    intentId: opaqueIdSchema,
    intentDigestSha256: sha256Schema,
    directionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    rendererBinding: bookProductionVersionBindingV1Schema,
    configurationBinding: bookProductionVersionBindingV1Schema,
    executions: z
        .array(z
        .object({
        executionId: opaqueIdSchema,
        semanticPageId: opaqueIdSchema,
        productPageRole: safeCodeSchema,
        grammarId: opaqueIdSchema,
        componentIds: z.array(opaqueIdSchema).min(1).max(100),
        tokenIds: z.array(opaqueIdSchema).min(1).max(100),
        consumedIntentSignalIds: z.array(opaqueIdSchema).max(200),
        consumedDirectionFlags: z
            .array(z
            .object({
            path: z.string().trim().min(1).max(300),
            valueDigestSha256: sha256Schema,
            productPageRole: safeCodeSchema,
            grammarId: opaqueIdSchema,
            componentId: opaqueIdSchema,
        })
            .strict())
            .max(300),
        metrics: bookVisualImplementationMetricsV1Schema,
        semanticFacts: z.array(semanticFactCodeSchema).max(100),
        visibleFields: z.array(bookVisualVisibleFieldUseV1Schema).max(100),
        outputDigestSha256: sha256Schema,
        status: z.literal("RENDERED"),
    })
        .strict())
        .min(1)
        .max(2_000),
    truthBoundary: z.literal("SUPPLIED_OBSERVATIONS_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.executions.map(({ executionId }) => executionId), context, ["executions"], "Trace execution IDs");
    value.executions.forEach((execution, index) => {
        unique(execution.componentIds, context, ["executions", index, "componentIds"], "Consumed component IDs");
        unique(execution.tokenIds, context, ["executions", index, "tokenIds"], "Consumed token IDs");
        unique(execution.consumedIntentSignalIds, context, ["executions", index, "consumedIntentSignalIds"], "Consumed intent signal IDs");
        unique(execution.consumedDirectionFlags.map(({ path, productPageRole, grammarId, componentId }) => `${path}:${productPageRole}:${grammarId}:${componentId}`), context, ["executions", index, "consumedDirectionFlags"], "Consumed direction flag observations");
        unique(execution.semanticFacts, context, ["executions", index, "semanticFacts"], "Execution semantic facts");
    });
});
export const bookVisualImplementationTraceV1Schema = bookVisualImplementationTraceBodyV1Schema.safeExtend({ traceDigestSha256: sha256Schema });
//# sourceMappingURL=book-visual-implementation.js.map